import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { ReporteService, type ResumenPlanillaData } from '../services/reporteService';

interface Props {
  open: boolean;
  onClose: () => void;
  idPlanilla: number;
  descripcionPlanilla: string;
}

const fmt = (n: number) => n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ResumenPlanillaDialog: React.FC<Props> = ({ open, onClose, idPlanilla, descripcionPlanilla }) => {
  const [resumen, setResumen] = useState<ResumenPlanillaData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && idPlanilla) {
      loadResumen();
    }
    return () => {
      setResumen(null);
      setError(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idPlanilla]);

  const loadResumen = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ReporteService.getResumen(idPlanilla);
      setResumen(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar resumen');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
      <head>
        <title>Resumen de Planilla - ${descripcionPlanilla}</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 8px 0; }
          th, td { border: 1px solid #ccc; padding: 4px 8px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: bold; }
          td.right, th.right { text-align: right; }
          .header { text-align: center; margin-bottom: 16px; }
          .section-title { font-weight: bold; margin: 12px 0 4px; padding: 4px 8px; }
          .totals-box { border: 2px solid #333; padding: 8px; margin-top: 16px; }
          @media print { body { margin: 10mm; } }
        </style>
      </head>
      <body>${printRef.current.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const renderConceptTable = (
    title: string,
    conceptos: ResumenPlanillaData['conceptosRemunerativos'],
    bgColor: string,
  ) => {
    if (!conceptos.length) return null;
    const total = conceptos.reduce((sum, c) => sum + c.montoTotal, 0);
    return (
      <>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, bgcolor: bgColor, px: 1, py: 0.5, borderRadius: 0.5 }}>
          {title}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={80}>Código</TableCell>
              <TableCell>Concepto</TableCell>
              <TableCell align="right" width={80}>Trab.</TableCell>
              <TableCell align="right" width={140}>Monto Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conceptos.map((c) => (
              <TableRow key={c.idConcepto}>
                <TableCell>{c.codigo}</TableCell>
                <TableCell>{c.descripcion}</TableCell>
                <TableCell align="right">{c.cantidadTrabajadores}</TableCell>
                <TableCell align="right">S/ {fmt(c.montoTotal)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3}><b>Total</b></TableCell>
              <TableCell align="right"><b>S/ {fmt(total)}</b></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Resumen de Planilla</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {resumen && (
          <Box ref={printRef}>
            {/* Cabecera */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">RESUMEN DE PLANILLA</Typography>
              <Typography variant="body1">{descripcionPlanilla}</Typography>
              <Typography variant="body2" color="text.secondary">
                {resumen.cantidadTrabajadores} trabajadores
              </Typography>
            </Box>

            <Divider />

            {/* Totales generales */}
            <Box sx={{ display: 'flex', gap: 2, my: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Paper sx={{ px: 2, py: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Total Remuneraciones</Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  S/ {fmt(resumen.totalRemuneraciones)}
                </Typography>
              </Paper>
              <Paper sx={{ px: 2, py: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Total Descuentos</Typography>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  S/ {fmt(resumen.totalDescuentos)}
                </Typography>
              </Paper>
              <Paper sx={{ px: 2, py: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Total Aportes</Typography>
                <Typography variant="h6" fontWeight="bold" color="warning.main">
                  S/ {fmt(resumen.totalAportes)}
                </Typography>
              </Paper>
              <Paper sx={{ px: 2, py: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Neto a Pagar</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  S/ {fmt(resumen.totalNeto)}
                </Typography>
              </Paper>
            </Box>

            <Divider />

            {/* Tablas por tipo de concepto */}
            {renderConceptTable('REMUNERACIONES', resumen.conceptosRemunerativos, '#e8f5e9')}
            {renderConceptTable('DESCUENTOS', resumen.conceptosDescuento, '#ffebee')}
            {renderConceptTable('APORTES DEL EMPLEADOR', resumen.conceptosAporte, '#fff3e0')}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={!resumen}
        >
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResumenPlanillaDialog;
